import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from './api';
import { keys } from '@/lib/query/keys';
import { DataView } from '@/lib/ui/states';
import { Card } from '@/lib/ui/Card';
import { TextInput } from '@/lib/ui/Field';
import { MonoValue } from '@/lib/ui/Typography';
import { Tag } from '@/lib/ui/Tag';
import { cn } from '@/lib/ui/cn';
import { PageCopyEditor } from './PageCopyEditor';

/**
 * Page-copy management (docs management.md §"Page copy"). Browse namespaces and edit each as
 * paired EN/AR fields. Namespaces and their fields are fixed by the site's authors — editors
 * change the copy, not the structure — so there's no create/delete here. Every write is audited
 * server-side and triggers an ISR revalidation of `/en` and `/ar`.
 *
 * Selection is **URL-driven** (`/settings/page-copy/:namespace`) so a namespace — and an
 * individual field via `focusField` — is shareable and reachable from the global search.
 */
export function PageCopyTab({
  selected,
  focusField = null,
  focusLocale = null,
  onSelect,
}: {
  selected: string | null;
  focusField?: string | null;
  /** Locale the search hit matched in, so deep-links land on the right-language copy. */
  focusLocale?: 'en' | 'ar' | null;
  onSelect: (namespace: string | null) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [filter, setFilter] = useState('');

  const list = useQuery({
    queryKey: keys.settings.pageCopy(),
    queryFn: () => settingsApi.listPageCopy(),
  });

  const namespaces = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const all = list.data ?? [];
    return q ? all.filter((n) => n.namespace.toLowerCase().includes(q)) : all;
  }, [list.data, filter]);

  const invalidate = () => qc.invalidateQueries({ queryKey: keys.settings.pageCopy() });

  return (
    <div className="grid gap-lg lg:grid-cols-[300px_1fr]">
      {/* Namespace list */}
      <div className="flex flex-col gap-sm">
        <TextInput
          type="search"
          value={filter}
          placeholder={t('settings.pageCopy.searchPlaceholder')}
          onChange={(e) => setFilter(e.target.value)}
          aria-label={t('settings.pageCopy.searchPlaceholder')}
        />
        <DataView query={list} isEmpty={() => namespaces.length === 0}>
          {() => (
            <ul className="divide-y divide-hairline overflow-hidden rounded-md border-[0.5px] border-hairline">
              {namespaces.map((ns) => (
                <li key={ns.namespace}>
                  <button
                    aria-current={selected === ns.namespace}
                    onClick={() => onSelect(ns.namespace)}
                    className={cn(
                      'flex w-full items-center justify-between gap-sm px-md py-sm text-start text-small hover:bg-canvas-sand',
                      selected === ns.namespace && 'bg-canvas-sand',
                    )}
                  >
                    <span className="flex min-w-0 flex-col gap-2xs">
                      <MonoValue className="truncate text-ink-deep">{ns.namespace}</MonoValue>
                      {ns.used_by_count === 0 && (
                        <span className="text-label-sm uppercase tracking-[0.08em] text-ink-mute">
                          {t('settings.pageCopy.unused')}
                        </span>
                      )}
                    </span>
                    {ns.ar_complete ? (
                      <Tag variant="teal">{t('common.arComplete')}</Tag>
                    ) : (
                      <Tag variant="amber">{t('common.missingArabic')}</Tag>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </DataView>
      </div>

      {/* Editor pane */}
      {selected ? (
        <NamespaceEditorLoader
          key={selected}
          namespace={selected}
          focusField={focusField}
          focusLocale={focusLocale}
          onSaved={invalidate}
        />
      ) : (
        <Card>
          <p className="text-small text-ink-mute">{t('settings.pageCopy.selectPrompt')}</p>
        </Card>
      )}
    </div>
  );
}

/** Fetches one namespace's full document, then seeds the editor's local state from it. */
function NamespaceEditorLoader({
  namespace,
  focusField,
  focusLocale,
  onSaved,
}: {
  namespace: string;
  focusField: string | null;
  focusLocale: 'en' | 'ar' | null;
  onSaved: () => void;
}) {
  const detail = useQuery({
    queryKey: keys.settings.pageCopyDetail(namespace),
    queryFn: () => settingsApi.getPageCopy(namespace),
  });
  return (
    <DataView query={detail}>
      {(data) => (
        <PageCopyEditor
          namespace={namespace}
          initial={data}
          focusField={focusField}
          focusLocale={focusLocale}
          onSaved={onSaved}
        />
      )}
    </DataView>
  );
}
